import {
  ColumnDef,
  Row,
  Table,
  getCoreRowModel,
  getExpandedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import React, {
  PropsWithChildren,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

import { Order, PickPartial } from './types';

// feature 별로 쪼개진 table 인터페이스 조각
interface TableDataProps<D> {
  cellOptions: ColumnDef<D, any>[];
  data: D[];
}

export interface PageableTableProps<D> {
  dataToShow: D[]; // dataToShow < (loaded) data < total data
  totalCount: number;
  countPerPage: number;
  countPerPageOptions: number[];
  page: number; // current page
  onChangePage?(page: number): void;
  onChangeCountPerPage?(countPerPage: number): void;
}

interface SelectableTableProps<D, K extends keyof D> {
  identifyKey: K;
  selected: readonly D[K][];
  isSelectable(element: D): boolean;
  onSelect?(key: D[K], checked: boolean): void;
  onSelectAll?(checked: boolean, selectables: D[]): void;
}

interface FocusableTableProps<D> {
  focused: D | null;
  onFocusData?(focused: D | null): void;
}

export interface SortableTableProps<D> {
  orderBy: keyof D;
  order: Order;
  onChangeOrder?(order: Order): void;
  onChangeOrderBy?(orderBy: keyof D, order?: Order): void;
}

interface UiOptions {
  isLoading?: boolean;
  disableSelectAll?: boolean;
  disableSelect?: boolean;
}

export interface TableElementProps<D> {
  element: D;
  elementIndex: number;
}

export interface TableElementInformation<D> extends TableElementProps<D> {
  isSelected: boolean;
  isSelectable: boolean;
  isFocused: boolean;
  row: Row<D>;
}
// -----------------

type OptionalProps =
  | 'cellOptions'
  | 'dataToShow'
  | 'totalCount'
  | 'countPerPage'
  | 'countPerPageOptions'
  | 'page'
  | 'selected'
  | 'isSelectable'
  | 'focused'
  | 'orderBy'
  | 'order';

export interface TableProps<D, K extends keyof D>
  extends TableDataProps<D>,
    PageableTableProps<D>,
    SelectableTableProps<D, K>,
    FocusableTableProps<D>,
    SortableTableProps<D>,
    UiOptions {}

// data 만 optional props 로 처리
// callback 함수들은 ? type 으로 처리
export type PresentationalTableProps<D, K extends keyof D> = PickPartial<
  TableProps<D, K>,
  OptionalProps
>;

export interface TableInformation<D, K extends keyof D>
  extends TableProps<D, K> {
  isSelected(element: D): boolean;
  isFocused(element: D): boolean;
  maxPage: number;
  selectables: D[];
  currentSelected: D[K][];
  table: Table<D>;
}

export const createTableInformationContext = <D, K extends keyof D>() => {
  const TableContext = React.createContext<TableInformation<D, K>>(
    undefined as any,
  );
  const TableInformationProvider = (
    props: PresentationalTableProps<D, K> & React.PropsWithChildren,
  ) => {
    const { children, ...other } = props;
    const {
      data,
      identifyKey,
      selected = [],
      focused = null,
      totalCount,
      countPerPage = 1,
      cellOptions = [],
      dataToShow = data,
      countPerPageOptions = [],
      page = 0,
      orderBy = identifyKey,
      order = 'desc',
      isSelectable = () => true,
    } = other;
    const isSelected = (element: D) => selected.includes(element[identifyKey]);
    const isFocused = (element: D) =>
      (focused && element[identifyKey] === focused[identifyKey]) || false;
    const selectables = useMemo(() => {
      return data.filter(element => isSelectable?.(element));
    }, [data, isSelectable]);

    const currentSelected = useMemo(() => {
      return selected.filter(s =>
        data.some(element => element[identifyKey] === s),
      );
    }, [data, identifyKey, selected]);

    const [memoizedTotalCount, setMemoizedTotalCount] = useState(0);
    useEffect(() => {
      if (totalCount !== undefined) {
        setMemoizedTotalCount(totalCount);
      }
    }, [totalCount]);

    const maxPage = useMemo(
      () => Math.ceil(memoizedTotalCount / countPerPage),
      [countPerPage, memoizedTotalCount],
    );

    const table = useReactTable({
      data: dataToShow,
      columns: cellOptions,
      getCoreRowModel: getCoreRowModel(),
      getExpandedRowModel: getExpandedRowModel(),
      getRowCanExpand: () => true,
    });

    return (
      <TableContext.Provider
        value={{
          ...other,
          order,
          orderBy,
          totalCount: memoizedTotalCount,
          countPerPage,
          countPerPageOptions,
          page,
          selected,
          focused,
          cellOptions,
          dataToShow,
          isSelected,
          isSelectable,
          isFocused,
          maxPage,
          selectables,
          currentSelected,
          table,
        }}>
        {children}
      </TableContext.Provider>
    );
  };
  return {
    TableContext,
    TableInformationProvider,
  };
};

export const createTableElementInformationContext = <D, K extends keyof D>(
  tableContext: React.Context<TableInformation<D, K>>,
) => {
  const TableElementContext = React.createContext<TableElementInformation<D>>(
    undefined as any,
  );
  const TableElementInformationProvider = (
    props: React.PropsWithChildren & TableElementProps<D>,
  ) => {
    const { children, element, elementIndex } = props;
    const {
      isSelected: _isSelected,
      isFocused: _isFocused,
      isSelectable: _isSelectable,
      table,
      ...others
    } = useContext(tableContext);
    const isSelected = _isSelected?.(element) ?? false;
    const isFocused = _isFocused?.(element) ?? false;
    const isSelectable = _isSelectable?.(element) ?? true;
    return (
      <TableElementContext.Provider
        value={{
          ...others,
          row: table.getRowModel().rows[elementIndex],
          isSelected,
          isFocused,
          isSelectable,
          element,
          elementIndex,
        }}>
        {children}
      </TableElementContext.Provider>
    );
  };

  return { TableElementContext, TableElementInformationProvider };
};

export type TableAndElementInformation<D, K extends keyof D> = Omit<
  TableInformation<D, K>,
  'isSelected' | 'isSelectable' | 'isFocused'
> &
  TableElementInformation<D>;

export interface RendererProps<D, K extends keyof D> {
  renderLoading?: (props: TableInformation<D, K>) => React.ReactNode;
  renderEmpty?: (props: TableInformation<D, K>) => React.ReactNode;
  Element?: (
    props: TableAndElementInformation<D, K>,
  ) => React.ReactElement | null;
  Header?: (props: TableInformation<D, K>) => React.ReactElement | null;
  Footer?: (props: TableInformation<D, K>) => React.ReactElement | null;
  TableBody?: (
    props: TableInformation<D, K> & PropsWithChildren,
  ) => React.ReactElement | null;
}

export interface RenderTableProps<D, K extends keyof D> {
  tableContext: React.Context<TableInformation<D, K>>;
  ElementProvider: (
    props: {
      children?: React.ReactNode;
    } & TableElementProps<D>,
  ) => React.ReactElement;
  elementContext: React.Context<TableElementInformation<D>>;
}
